import { ACTIVITY_KIND_LABEL, conversionFactor, type Scenario } from "@h2lca/core";
import type { ImportRecord } from "@/components/workspace/import-template";

export interface ImportMatch {
  record: ImportRecord;
  rowIndex: number;
  equipmentId: string;
  equipmentName: string;
  inputId: string;
  inputLabel: string;
  conversionFactor: number;
}

export interface ImportUnmatched {
  record: ImportRecord;
  reason: string;
}

export interface ImportMapping {
  matches: ImportMatch[];
  unmatched: ImportUnmatched[];
}

function normalize(value: string) {
  return value.toLocaleLowerCase("ko-KR").replace(/[^\p{L}\p{N}]+/gu, "");
}

function scoreText(target: string, candidate: string) {
  if (!target || !candidate) return 0;
  if (target === candidate) return 5;
  if (target.includes(candidate) || candidate.includes(target)) return 3;
  return 0;
}

export function suggestImportMapping(records: ImportRecord[], scenario: Scenario): ImportMapping {
  const candidates: ImportMatch[] = [];
  const unmatched: ImportUnmatched[] = [];

  for (const [rowIndex, record] of records.entries()) {
    if (record.amount === null || !Number.isFinite(record.amount) || record.amount < 0) {
      unmatched.push({ record, reason: "숫자값을 확인해야 합니다" });
      continue;
    }
    if (!record.period || !record.source) {
      unmatched.push({ record, reason: "기간과 출처를 확인한 뒤 자동 반영합니다" });
      continue;
    }
    const equipmentName = normalize(record.equipment);
    const activityName = normalize(record.activity);
    if (!equipmentName) {
      unmatched.push({ record, reason: "설비명이 없어 자동 연결하지 않았습니다" });
      continue;
    }
    if (!activityName) {
      unmatched.push({ record, reason: "활동자료 이름이 없어 자동 연결하지 않았습니다" });
      continue;
    }

    const equipmentMatches = scenario.equipment.filter((equipment) => {
      const name = normalize(equipment.name);
      return name === equipmentName || name.includes(equipmentName) || equipmentName.includes(name);
    });
    if (equipmentMatches.length !== 1) {
      unmatched.push({ record, reason: equipmentMatches.length === 0 ? "일치하는 설비를 찾지 못했습니다" : "일치하는 설비가 여러 개입니다" });
      continue;
    }

    const equipment = equipmentMatches[0];
    if (!equipment) {
      unmatched.push({ record, reason: "일치하는 설비를 찾지 못했습니다" });
      continue;
    }
    const inputMatches = equipment.inputs
      .map((input) => {
        const labelScore = scoreText(activityName, normalize(input.label));
        const keyScore = scoreText(activityName, normalize(input.key));
        const kindScore = scoreText(activityName, normalize(ACTIVITY_KIND_LABEL[input.kind]));
        const unitScore = normalize(record.unit) === normalize(input.unit) ? 2 : conversionFactor(record.unit, input.unit) !== null ? 1 : 0;
        return { input, score: Math.max(labelScore, keyScore, kindScore) + unitScore };
      })
      .filter(({ input, score }) => score >= 5 && conversionFactor(record.unit, input.unit) !== null)
      .sort((a, b) => b.score - a.score);
    if (inputMatches.length === 0) {
      unmatched.push({ record, reason: "설비 안에서 일치하는 활동자료와 단위를 찾지 못했습니다" });
      continue;
    }
    const best = inputMatches[0];
    const second = inputMatches[1];
    if (best && second && best.score === second.score) {
      unmatched.push({ record, reason: "일치하는 활동자료가 여러 개입니다" });
      continue;
    }

    if (!best) {
      unmatched.push({ record, reason: "일치하는 활동자료를 찾지 못했습니다" });
      continue;
    }
    const input = best.input;
    candidates.push({ record, rowIndex, equipmentId: equipment.id, equipmentName: equipment.name, inputId: input.id, inputLabel: input.label, conversionFactor: conversionFactor(record.unit, input.unit) ?? 1 });
  }

  const byInput = new Map<string, ImportMatch[]>();
  for (const match of candidates) byInput.set(match.inputId, [...(byInput.get(match.inputId) ?? []), match]);
  const matches: ImportMatch[] = [];
  for (const group of byInput.values()) {
    if (group.length === 1 && group[0]) matches.push(group[0]);
    else group.forEach((match) => unmatched.push({ record: match.record, reason: "같은 산정 입력값에 여러 행이 있어 자동 반영하지 않았습니다" }));
  }

  return { matches, unmatched };
}
